import { Box } from "@chakra-ui/react";
import type { ReactElement } from "react";
import { Navigate } from "react-router-dom";
import { Home } from "./home/Home";
import { NotificationsPageV2 } from "./notifications/NotificationsPageV2";
import { ContractStatusDto } from "../../autogen/bff-api";
import { AssistantPage } from "../../common/agent/AssistantPage";
import { Admin } from "../admin";
import { urls } from "../../urls";
import { EditContract } from "../contracts/edit/EditContract";
import { ContractSearchPageV2 } from "../contracts/search-v2/ContractSearchPageV2";
import { Contracts } from "../contracts/view-all/Contracts";
import { ContractTasks } from "../contracts/view-all-tasks/ContractTasks";
import { ContractPage } from "../contracts/view-single/ContractPage";
import { AccessManagementPage } from "../organizations/current/manage-users/AccessManagementPage";
import { EditOrganizationProfile } from "../organizations/current/edit/EditOrganizationProfile";
import { OrganizationsPage } from "../organizations/search/OrganizationsPage";
import { ViewOrganizationProfile } from "../organizations/view-single/ViewOrganizationProfile";
import { Projects } from "../projects/view-all/Projects";
import { ProjectPage } from "../projects/view-single/ProjectPage";
import { EditBasicSourcingEvent } from "../sourcing-events/buyer/edit/EditBasicSourcingEvent";
import { EditSourcingEventWizard } from "../sourcing-events/buyer/wizard/EditSourcingEventWizard";
import { LegacyWizardRedirect } from "../sourcing-events/buyer/wizard/LegacyWizardRedirect";
import { BasicSourcingEventView } from "../sourcing-events/buyer/view-single/BasicSourcingEventView";
import { EditBidV2Page } from "../sourcing-events/portal/bid/EditBidV2Page";
import { EditPrivateBidPage } from "../sourcing-events/portal/bid/EditPrivateBidPage";
import { EditParticipationRequestPage } from "../sourcing-events/portal/request/EditParticipationRequestPage";
import { PortalSourcingEventsPage } from "../sourcing-events/portal/view-all/PortalSourcingEventsPage";
import { PortalSourcingEventPage } from "../sourcing-events/portal/view-single/PortalSourcingEventPage";
import { SupplierBidsList } from "./supplier/SupplierBidsList";
import { SupplierInvitationsList } from "./supplier/SupplierInvitationsList";
import { ContributingEntityPage } from "../sourcing-events/portal/contributing/ContributingEntityPage";
import { ContributingEntityResponsePage } from "../sourcing-events/portal/contributing/ContributingEntityResponsePage";
import { SupplierContributionsList } from "../sourcing-events/portal/contributing/SupplierContributionsList";
import { Behovsliste } from "./behov/Behovsliste";
import { NeedsRoute } from "./behov/NeedsRoute";
import { EditNeed } from "./behov/EditNeed";
import { MineBehov } from "./behov/MineBehov";
import { NeedView } from "./behov/NeedView";
import { NewNeed } from "./behov/NewNeed";
import { EditProfilePage } from "./profile/EditProfilePage";
import { ProfilePage } from "./profile/ProfilePage";
import { SettingsPage } from "./settings/SettingsPage";
import { Kravbibliotek } from "./kravbibliotek/Kravbibliotek";
import { MalEditor } from "./maler/MalEditor";
import { SourcingEventTemplates } from "./maler/SourcingEventTemplates";
import { SourcingEventsList } from "./SourcingEventsList";
import { BidDetailView } from "./sourcing-event/bid-detail/BidDetailView";
import { EditNegotiationRoundWizard } from "./sourcing-event/negotiation/wizard/EditNegotiationRoundWizard";
import { RequestDetailView } from "./sourcing-event/qualification/detail/RequestDetailView";
import { ProtocolPage } from "./sourcing-event/protocol/ProtocolPage";
import { SourcingEventView } from "./sourcing-event/SourcingEventView";

type V2Route = { path: string; element: ReactElement; fullWidth?: boolean; fillHeight?: boolean };

const contributingEntityRoutes: V2Route[] = [
  { path: urls.v2.supplier.contributions, element: <SupplierContributionsList />, fullWidth: true },
  { path: urls.v2.supplier.contribution.pathname, element: <ContributingEntityPage /> },
  {
    path: urls.v2.supplier.contributionReview.pathname,
    element: <ContributingEntityResponsePage />,
  },
];

export const v2Routes: V2Route[] = [
  { path: urls.admin, element: <Admin />, fullWidth: true },
  { path: urls.v2.home, element: <Home /> },
  { path: urls.v2.notifications, element: <NotificationsPageV2 /> },
  { path: urls.v2.assistant, element: <AssistantPage /> },
  { path: urls.v2.needs.new, element: <NeedsRoute><NewNeed /></NeedsRoute> },
  { path: urls.v2.needs.edit.pathname, element: <NeedsRoute><EditNeed /></NeedsRoute> },
  { path: urls.v2.needs.view.pathname, element: <NeedsRoute><NeedView /></NeedsRoute> },
  { path: urls.v2.needs.mine, element: <NeedsRoute><MineBehov /></NeedsRoute>, fullWidth: true },
  { path: urls.v2.needs.list, element: <NeedsRoute><Behovsliste /></NeedsRoute>, fullWidth: true },
  { path: urls.v2.sourcingEvents.viewAll, element: <SourcingEventsList />, fullWidth: true },
  { path: urls.v2.kravbibliotek, element: <Kravbibliotek />, fullWidth: true },
  { path: urls.v2.sourcingEvents.view.pathname, element: <SourcingEventView /> },
  { path: urls.v2.sourcingEvents.bidDetail.pathname, element: <BidDetailView /> },
  { path: urls.v2.sourcingEvents.participationRequestDetail.pathname, element: <RequestDetailView /> },
  { path: urls.v2.sourcingEvents.protocol.pathname, element: <ProtocolPage /> },
  { path: urls.v2.supplier.procurements, element: <PortalSourcingEventsPage variant="browse" />, fullWidth: true },
  { path: urls.v2.supplier.procurement.pathname, element: <PortalSourcingEventPage /> },
  { path: urls.v2.supplier.bids, element: <SupplierBidsList />, fullWidth: true },
  { path: urls.v2.supplier.bid.edit.paths.intro.pathname, element: <EditBidV2Page /> },
  { path: urls.v2.supplier.privateBid.edit.pathname, element: <EditPrivateBidPage /> },
  { path: urls.v2.supplier.invitations, element: <SupplierInvitationsList />, fullWidth: true },
  { path: "/v2/mine-foresporsler", element: <Navigate to={urls.v2.supplier.invitations} replace /> },
  { path: urls.v2.supplier.request.edit.paths.intro.pathname, element: <EditParticipationRequestPage /> },
  {
    path: urls.v2.sourcingEvents.bseView.pathname,
    element: (
      <Box bg="smBackground" rounded="lg" border="1px solid" borderColor="smBorder" px={{ base: "4", md: "6" }} py="6">
        <BasicSourcingEventView embedded />
      </Box>
    ),
  },
  {
    path: urls.v2.sourcingEvents.edit.paths.info.pathname,
    element: (
      <Box bg="smBackground" rounded="lg" border="1px solid" borderColor="smBorder" px={{ base: "4", md: "6" }} py="6">
        <EditSourcingEventWizard />
      </Box>
    ),
  },
  {
    path: urls.v2.sourcingEvents.edit.legacyPathname,
    element: <LegacyWizardRedirect />,
  },
  {
    path: urls.v2.sourcingEvents.editNegotiationRound.pathname,
    element: (
      <Box bg="smBackground" rounded="lg" border="1px solid" borderColor="smBorder" px={{ base: "4", md: "6" }} py="6">
        <EditNegotiationRoundWizard />
      </Box>
    ),
  },
  {
    path: urls.v2.sourcingEvents.bseEdit.pathname,
    element: (
      <Box bg="smBackground" rounded="lg" border="1px solid" borderColor="smBorder" px={{ base: "4", md: "6" }} py="6">
        <EditBasicSourcingEvent embedded />
      </Box>
    ),
  },
  {
    path: urls.v2.contracts.viewAll,
    element: (
      <Box bg="smBackground" rounded="lg" border="1px solid" borderColor="smBorder">
        <Contracts key="contracts-all" embedded />
      </Box>
    ),
    fullWidth: true,
  },
  {
    path: urls.v2.contracts.active,
    element: (
      <Box bg="smBackground" rounded="lg" border="1px solid" borderColor="smBorder">
        <Contracts key="contracts-active" embedded defaultStatuses={[ContractStatusDto.Active, ContractStatusDto.Awarded]} />
      </Box>
    ),
    fullWidth: true,
  },
  {
    path: urls.v2.contracts.search,
    element: <ContractSearchPageV2 />,
    fillHeight: true,
  },
  {
    path: urls.v2.contracts.tasks,
    element: (
      <Box bg="smBackground" rounded="lg" border="1px solid" borderColor="smBorder" px={{ base: "4", md: "6" }} py="6">
        <ContractTasks embedded />
      </Box>
    ),
    fullWidth: true,
  },
  {
    path: urls.v2.contracts.view.pathname,
    element: (
      <Box bg="smBackground" rounded="lg" border="1px solid" borderColor="smBorder" px={{ base: "4", md: "6" }} py="6">
        <ContractPage embedded />
      </Box>
    ),
  },
  {
    path: urls.v2.contracts.edit.pathname,
    element: (
      <Box bg="smBackground" rounded="lg" border="1px solid" borderColor="smBorder" px={{ base: "4", md: "6" }} py="6">
        <EditContract embedded />
      </Box>
    ),
  },
  {
    path: urls.v2.projects.viewAll,
    element: (
      <Box bg="smBackground" rounded="lg" border="1px solid" borderColor="smBorder">
        <Projects embedded />
      </Box>
    ),
    fullWidth: true,
  },
  {
    path: urls.v2.projects.view.pathname,
    element: (
      <Box bg="smBackground" rounded="lg" border="1px solid" borderColor="smBorder" px={{ base: "4", md: "6" }} py="6">
        <ProjectPage embedded />
      </Box>
    ),
  },
  { path: urls.v2.profile, element: <ProfilePage /> },
  { path: urls.v2.profileEdit, element: <EditProfilePage /> },
  { path: urls.v2.settings, element: <SettingsPage /> },
  { path: urls.v2.maler.list, element: <SourcingEventTemplates />, fullWidth: true },
  { path: urls.v2.maler.new.pathname, element: <MalEditor /> },
  { path: urls.v2.maler.edit.pathname, element: <MalEditor /> },
  {
    path: urls.v2.users,
    element: (
      <Box bg="smBackground" rounded="lg" border="1px solid" borderColor="smBorder" px="6" pb="6">
        <AccessManagementPage embedded />
      </Box>
    ),
    fullWidth: true,
  },
  {
    path: urls.v2.organizations,
    element: (
      <Box bg="smBackground" rounded="lg" border="1px solid" borderColor="smBorder" px={{ base: "4", md: "6" }} py="2">
        <OrganizationsPage embedded />
      </Box>
    ),
    fullWidth: true,
  },
  {
    path: `${urls.v2.organizationProfile.pathname}/*`,
    element: <ViewOrganizationProfile embedded />,
  },
  {
    path: urls.v2.organizationProfileEdit,
    element: (
      <Box bg="smBackground" rounded="lg" border="1px solid" borderColor="smBorder" px={{ base: "4", md: "6" }} pb="6">
        <EditOrganizationProfile embedded />
      </Box>
    ),
  },
  ...contributingEntityRoutes,
];

